import React, { useState, useCallback } from 'react';
import { jsPDF } from 'jspdf';

// Calculation assumptions
const ASSUMPTIONS = {
  vacancyCostMultiplier: 2.0,
  productivityLossPercent: 0.25,
  productivityLossDuration: 6, // months
  memberSatisfactionImpact: 0.5,
  affectedMemberPercent: 0.10,
  complianceRiskPercent: 0.001,
  manualProcessHourlyRate: 75
};

// Format number with commas
const formatNumber = (num) => {
  if (!num && num !== 0) return '';
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

// Format as currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

// Parse formatted number (remove commas)
const parseFormattedNumber = (str) => {
  if (!str) return 0;
  return parseFloat(str.toString().replace(/[,$]/g, '')) || 0;
};

// Currency Input Component
const CurrencyInput = ({ value, onChange, onBlur, placeholder, helpText }) => {
  const handleChange = (e) => {
    const rawValue = e.target.value.replace(/[^0-9]/g, '');
    onChange(rawValue);
  };

  return (
    <div>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">$</span>
        <input
          type="text"
          value={value ? formatNumber(value) : ''}
          onChange={handleChange}
          onBlur={onBlur}
          placeholder={placeholder}
          className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-lg text-base focus:outline-none focus:border-blue-700 focus:ring-2 focus:ring-blue-100 transition-all"
        />
      </div>
      {helpText && <p className="mt-1 text-sm text-gray-500 italic">{helpText}</p>}
    </div>
  );
};

// Number Input Component
const NumberInput = ({ value, onChange, placeholder, helpText }) => {
  const handleChange = (e) => {
    const rawValue = e.target.value.replace(/[^0-9.]/g, '');
    onChange(rawValue);
  };

  return (
    <div>
      <input
        type="text"
        value={value ? formatNumber(value) : ''}
        onChange={handleChange}
        placeholder={placeholder}
        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-base focus:outline-none focus:border-blue-700 focus:ring-2 focus:ring-blue-100 transition-all"
      />
      {helpText && <p className="mt-1 text-sm text-gray-500 italic">{helpText}</p>}
    </div>
  );
};

// Percent Input Component
const PercentInput = ({ value, onChange, placeholder, helpText }) => {
  const handleChange = (e) => {
    let rawValue = e.target.value.replace(/[^0-9.]/g, '');
    if (parseFloat(rawValue) > 100) rawValue = '100';
    onChange(rawValue);
  };

  return (
    <div>
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          className="w-full pl-4 pr-8 py-3 border-2 border-gray-200 rounded-lg text-base focus:outline-none focus:border-blue-700 focus:ring-2 focus:ring-blue-100 transition-all"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">%</span>
      </div>
      {helpText && <p className="mt-1 text-sm text-gray-500 italic">{helpText}</p>}
    </div>
  );
};

// Cost Item Row
const CostItem = ({ label, value }) => (
  <div className="flex justify-between py-3 border-b border-gray-100 last:border-b-0">
    <span className="text-gray-600">{label}</span>
    <span className="font-semibold text-gray-800">{formatCurrency(value)}</span>
  </div>
);

// Warning Box Component
const WarningBox = ({ warnings }) => {
  if (!warnings || warnings.length === 0) return null;

  return (
    <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-4 rounded-lg mb-6">
      <strong className="block mb-1">High Risk Alert:</strong>
      <span>{warnings.join('. ')}</span>
    </div>
  );
};

// Get pricing based on employee count
const getPriceByEmployees = (employeeCount) => {
  const count = parseInt(employeeCount) || 0;
  if (count <= 25) return 1995;
  if (count <= 100) return 3495;
  if (count <= 200) return 5995;
  if (count <= 300) return 7495;
  if (count <= 400) return 8495;
  if (count <= 500) return 9495;
  if (count <= 800) return 11995;
  return 14995;
};

// Get employee tier label
const getEmployeeTier = (employeeCount) => {
  const count = parseInt(employeeCount) || 0;
  if (count <= 25) return '0-25 employees';
  if (count <= 100) return '26-100 employees';
  if (count <= 200) return '101-200 employees';
  if (count <= 300) return '201-300 employees';
  if (count <= 400) return '301-400 employees';
  if (count <= 500) return '401-500 employees';
  if (count <= 800) return '501-800 employees';
  return '801+ employees';
};

const ROICalculator = () => {
  // Form inputs state
  const [inputs, setInputs] = useState({
    totalAssets: '',
    numberOfMembers: '',
    numberOfEmployees: '',
    operatingBudget: '',
    keyPositions: '8',
    retirementEligible: '',
    timeToFill: '120',
    turnoverRate: '12',
    currentApproach: 'none',
    avgExecSalary: '',
    searchCostPercent: '25',
    memberSatisfaction: '4.2',
    marketShare: '20',
    manualHours: '200'
  });

  // Results state
  const [results, setResults] = useState(null);
  const [warnings, setWarnings] = useState([]);

  // Handle input change
  const handleInputChange = useCallback((field, value) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  }, []);

  // Auto-estimate values based on assets
  const handleAssetsBlur = useCallback(() => {
    const assets = parseFormattedNumber(inputs.totalAssets);
    if (assets > 0) {
      setInputs(prev => ({
        ...prev,
        numberOfMembers: prev.numberOfMembers || String(Math.round(assets / 6000)),
        numberOfEmployees: prev.numberOfEmployees || String(Math.round(assets / 2000000)),
        operatingBudget: prev.operatingBudget || String(Math.round(assets * 0.08))
      }));
    }
  }, [inputs.totalAssets]);

  // Calculate ROI
  const calculateROI = useCallback(() => {
    const data = {
      totalAssets: parseFormattedNumber(inputs.totalAssets) || 150000000,
      numberOfMembers: parseInt(inputs.numberOfMembers) || 25000,
      numberOfEmployees: parseInt(inputs.numberOfEmployees) || 75,
      operatingBudget: parseFormattedNumber(inputs.operatingBudget) || 12000000,
      keyPositions: parseInt(inputs.keyPositions) || 8,
      retirementEligible: parseInt(inputs.retirementEligible) || 3,
      timeToFill: parseInt(inputs.timeToFill) || 120,
      turnoverRate: parseFloat(inputs.turnoverRate) || 12,
      currentApproach: inputs.currentApproach,
      avgExecSalary: parseFormattedNumber(inputs.avgExecSalary) || 175000,
      searchCostPercent: parseFloat(inputs.searchCostPercent) || 25,
      memberSatisfaction: parseFloat(inputs.memberSatisfaction) || 4.2,
      marketShare: parseFloat(inputs.marketShare) || 20,
      manualHours: parseInt(inputs.manualHours) || 200
    };

    // Calculate annual turnover
    const annualTurnover = Math.max(1, Math.round(data.keyPositions * (data.turnoverRate / 100)));

    // 1. Vacancy Costs
    const vacancyDays = data.timeToFill;
    const vacancyCostPerPosition = (data.avgExecSalary * ASSUMPTIONS.vacancyCostMultiplier) * (vacancyDays / 365);
    const totalVacancyCost = vacancyCostPerPosition * annualTurnover;

    // 2. Recruitment Costs
    const recruitmentCostPerHire = data.avgExecSalary * (data.searchCostPercent / 100);
    const totalRecruitmentCost = recruitmentCostPerHire * annualTurnover;

    // 3. Productivity Loss
    const productivityLossPerTransition = data.avgExecSalary * ASSUMPTIONS.productivityLossPercent *
      (ASSUMPTIONS.productivityLossDuration / 12);
    const totalProductivityLoss = productivityLossPerTransition * annualTurnover;

    // 4. Member Impact
    const avgMemberValue = data.operatingBudget / data.numberOfMembers;
    const memberImpact = data.numberOfMembers * ASSUMPTIONS.affectedMemberPercent *
      avgMemberValue * (ASSUMPTIONS.memberSatisfactionImpact / 5);

    // 5. Manual Process Cost
    const manualProcessCost = data.manualHours * ASSUMPTIONS.manualProcessHourlyRate;

    // 6. Compliance Risk
    const complianceRisk = data.totalAssets * ASSUMPTIONS.complianceRiskPercent;

    // Total costs
    const totalAnnualCost = totalVacancyCost + totalRecruitmentCost +
      totalProductivityLoss + memberImpact +
      manualProcessCost + complianceRisk;

    // Additional metrics
    const costPerMember = totalAnnualCost / data.numberOfMembers;
    const percentOfBudget = (totalAnnualCost / data.operatingBudget) * 100;
    const fiveYearImpact = totalAnnualCost * 5;

    // Check for warnings
    const newWarnings = [];
    if (data.retirementEligible >= data.keyPositions * 0.5) {
      newWarnings.push(`${data.retirementEligible} of ${data.keyPositions} key leaders are retirement eligible - critical succession risk`);
    }
    if (data.timeToFill > 150) {
      newWarnings.push(`Your ${data.timeToFill}-day average to fill positions is well above the 90-day best practice`);
    }
    if (data.currentApproach === 'none') {
      newWarnings.push('No formal succession planning significantly increases regulatory and operational risk');
    }

    setWarnings(newWarnings);
    setResults({
      vacancyCost: totalVacancyCost,
      recruitmentCost: totalRecruitmentCost,
      productivityCost: totalProductivityLoss,
      memberImpactCost: memberImpact,
      manualProcessCost: manualProcessCost,
      complianceRisk: complianceRisk,
      totalAnnualCost: totalAnnualCost,
      costPerMember: costPerMember,
      percentOfBudget: percentOfBudget,
      fiveYearImpact: fiveYearImpact,
      inputs: data
    });
  }, [inputs]);

  // Export JSON
  const exportJSON = useCallback(() => {
    if (!results) return;

    const exportData = {
      version: "1.0",
      tool: "Succession Planning Cost Calculator",
      timestamp: new Date().toISOString(),
      creditUnion: {
        assets: results.inputs.totalAssets,
        members: results.inputs.numberOfMembers,
        employees: results.inputs.numberOfEmployees
      },
      summary: {
        annualCostOfPoorSuccessionPlanning: results.totalAnnualCost,
        costPerMember: results.costPerMember,
        percentageOfBudget: results.percentOfBudget,
        fiveYearImpact: results.fiveYearImpact
      },
      calculations: results
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const fileName = `succession-cost-analysis-${new Date().toISOString().split('T')[0]}.json`;

    const link = document.createElement('a');
    link.setAttribute('href', dataUri);
    link.setAttribute('download', fileName);
    link.click();
  }, [results]);

  // Generate PDF report
  const generatePDF = useCallback(() => {
    if (!results) return;

    const doc = new jsPDF('portrait', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);
    let yPos = 20;

    // ============ PAGE 1 ============
    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 60, 114); // Dark blue
    doc.text('Your Succession Planning Cost Analysis', margin, yPos);
    yPos += 15;

    // Cost Breakdown Section - Box with border
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.5);
    doc.roundedRect(margin, yPos, contentWidth, 75, 3, 3, 'S');

    yPos += 8;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(40, 40, 40);
    doc.text('Annual Costs WITHOUT Succession Planning Software', margin + 5, yPos);
    yPos += 10;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);

    const costs = [
      ['Leadership Vacancy Costs', results.vacancyCost],
      ['External Search & Recruitment', results.recruitmentCost],
      ['Productivity Loss During Transitions', results.productivityCost],
      ['Member Impact & Market Share Loss', results.memberImpactCost],
      ['Manual Process Time Cost', results.manualProcessCost],
      ['Regulatory & Compliance Risk', results.complianceRisk]
    ];

    costs.forEach(([label, value]) => {
      doc.text(label, margin + 5, yPos);
      doc.text(formatCurrency(value), pageWidth - margin - 5, yPos, { align: 'right' });
      yPos += 9;
    });

    yPos += 12;

    // Total Cost (yellow highlighted box)
    doc.setFillColor(255, 247, 205); // Light yellow
    doc.roundedRect(margin, yPos, contentWidth, 35, 3, 3, 'F');

    yPos += 10;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(40, 40, 40);
    doc.text('Total Annual Cost of Poor Succession Planning', pageWidth / 2, yPos, { align: 'center' });

    yPos += 12;
    doc.setFontSize(24);
    doc.setTextColor(180, 0, 0); // Red
    doc.text(formatCurrency(results.totalAnnualCost), pageWidth / 2, yPos, { align: 'center' });

    yPos += 8;
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text('Per Year', pageWidth / 2, yPos, { align: 'center' });

    yPos += 18;

    // What This Means - Green bordered box
    doc.setDrawColor(34, 197, 94); // Green
    doc.setLineWidth(1);
    doc.setFillColor(240, 253, 244); // Light green bg
    doc.roundedRect(margin, yPos, contentWidth, 45, 3, 3, 'FD');

    yPos += 10;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(40, 40, 40);
    doc.text('What This Means for Your Credit Union', margin + 5, yPos);
    yPos += 10;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);

    doc.text('Cost per Member', margin + 5, yPos);
    doc.text(formatCurrency(results.costPerMember), pageWidth - margin - 5, yPos, { align: 'right' });
    yPos += 8;

    doc.text('Percentage of Operating Budget', margin + 5, yPos);
    doc.text(`${results.percentOfBudget.toFixed(1)}%`, pageWidth - margin - 5, yPos, { align: 'right' });
    yPos += 8;

    doc.text('5-Year Cumulative Impact', margin + 5, yPos);
    doc.text(formatCurrency(results.fiveYearImpact), pageWidth - margin - 5, yPos, { align: 'right' });

    // ============ PAGE 2 ============
    doc.addPage();
    yPos = 20;

    // Reduce These Costs Section - Teal box
    doc.setFillColor(54, 216, 191); // dark-teal-400
    doc.roundedRect(margin, yPos, contentWidth, 75, 3, 3, 'F');

    yPos += 12;
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('Reduce These Costs', margin + 8, yPos);
    yPos += 10;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('SuccessionNow automates the succession planning process, helping to reduce these costs and save time.', margin + 8, yPos);
    yPos += 12;

    // White box for quote inside teal - centered layout matching site
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(margin + 8, yPos - 2, contentWidth - 16, 38, 3, 3, 'F');

    // "Your Quote" centered at top
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(60, 60, 60);
    doc.text('Your Quote', pageWidth / 2, yPos + 6, { align: 'center' });

    // Large price centered
    doc.setFontSize(24);
    doc.setTextColor(54, 216, 191); // dark-teal
    const quotePrice = formatCurrency(getPriceByEmployees(results.inputs.numberOfEmployees));
    doc.text(quotePrice, pageWidth / 2, yPos + 20, { align: 'center' });

    // "per year" centered below price
    doc.setFontSize(11);
    doc.setTextColor(100, 100, 100);
    doc.text('per year', pageWidth / 2, yPos + 28, { align: 'center' });

    yPos += 55;

    // Strategic Opportunities Section - Box with border
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.5);
    doc.roundedRect(margin, yPos, contentWidth, 75, 3, 3, 'S');

    yPos += 8;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(40, 40, 40);
    doc.text('Strategic Opportunities with Better Succession Planning', margin + 5, yPos);
    yPos += 10;

    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);

    const opportunities = [
      ['Reduced Vacancy Time:', 'Fill critical roles faster with ready successors'],
      ['Less External Recruitment:', 'Develop and promote internal talent'],
      ['Improved NCUA Examinations:', 'Demonstrate systematic succession planning'],
      ['Enhanced Board Governance:', 'Provide clear oversight and risk management'],
      ['Increased Employee Retention:', 'Show clear career advancement paths'],
      ['Better Member Service:', 'Maintain consistency during leadership transitions'],
      ['Preserved Market Share:', 'Avoid disruption that competitors can exploit'],
      ['Reduced Key Person Risk:', 'Build depth in critical positions']
    ];

    opportunities.forEach(([title, desc]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(`• ${title}`, margin + 5, yPos);
      doc.setFont('helvetica', 'normal');
      const titleWidth = doc.getTextWidth(`• ${title} `);
      doc.text(desc, margin + 5 + titleWidth, yPos);
      yPos += 7;
    });

    yPos += 15;

    // Key Assumptions Section - Yellow box
    doc.setFillColor(255, 251, 235); // Light yellow/cream
    doc.setDrawColor(251, 191, 36); // Yellow border
    doc.setLineWidth(0.5);
    doc.roundedRect(margin, yPos, contentWidth, 58, 3, 3, 'FD');

    yPos += 8;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(180, 130, 50); // Golden brown
    doc.text('Key Assumptions & Methodology', margin + 5, yPos);
    yPos += 8;

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);

    const assumptions = [
      'Vacancy costs calculated at 2x salary for duration of vacancy (includes interim coverage & overtime)',
      'Productivity loss estimated at 25% for 6 months during leadership transitions',
      'Member impact based on 0.5-point satisfaction drop affecting 10% of member base',
      'External recruitment costs typically 20-35% of first-year salary',
      'Compliance risk estimated at 0.1% of assets for inadequate succession planning',
      'Manual process time valued at $75 per hour for board and executive time',
      'All calculations based on credit union industry benchmarks'
    ];

    assumptions.forEach((assumption) => {
      doc.text(`• ${assumption}`, margin + 5, yPos);
      yPos += 6;
    });

    // Contact Information (PDF only) - with extra space
    yPos += 15;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text('If you would like more information on SuccessionNow please reach out to', pageWidth / 2, yPos, { align: 'center' });
    yPos += 6;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(54, 216, 191); // teal for email
    doc.text('contact@successionnow.com', pageWidth / 2, yPos, { align: 'center' });
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text('or visit our website to request a demo.', pageWidth / 2, yPos, { align: 'center' });

    // Footer on page 2
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text('Generated by SuccessionNow - The NEXT Method', pageWidth / 2, pageHeight - 10, { align: 'center' });

    // Save the PDF
    const fileName = `succession-cost-analysis-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  }, [results]);

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-800 to-blue-900 text-white px-8 py-10 text-center">
        <h2 className="text-2xl md:text-3xl font-bold mb-2">What Poor Succession Planning Really Costs You</h2>
        <p className="text-blue-100">Discover the true cost of inadequate succession planning in under 10 minutes</p>
      </div>

      <div className="p-6 md:p-10">
        {/* Section 1: Basic Information */}
        <div className="mb-8 p-6 bg-gray-50 rounded-xl border-l-4 border-blue-700">
          <h3 className="text-xl font-semibold text-blue-800 mb-6">Step 1: Your Credit Union Information</h3>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block mb-2 text-gray-700 font-semibold text-sm">Total Assets</label>
              <CurrencyInput
                value={inputs.totalAssets}
                onChange={(v) => handleInputChange('totalAssets', v)}
                onBlur={handleAssetsBlur}
                placeholder="150,000,000"
                helpText="Enter your total assets (e.g., $150,000,000 for $150M)"
              />
            </div>

            <div>
              <label className="block mb-2 text-gray-700 font-semibold text-sm">Number of Members</label>
              <NumberInput
                value={inputs.numberOfMembers}
                onChange={(v) => handleInputChange('numberOfMembers', v)}
                placeholder="25,000"
                helpText="Total member count from your latest report"
              />
            </div>

            <div>
              <label className="block mb-2 text-gray-700 font-semibold text-sm">Number of Employees</label>
              <NumberInput
                value={inputs.numberOfEmployees}
                onChange={(v) => handleInputChange('numberOfEmployees', v)}
                placeholder="75"
                helpText="Total FTE count"
              />
            </div>

            <div>
              <label className="block mb-2 text-gray-700 font-semibold text-sm">Annual Operating Budget</label>
              <CurrencyInput
                value={inputs.operatingBudget}
                onChange={(v) => handleInputChange('operatingBudget', v)}
                placeholder="12,000,000"
                helpText="Total annual operating expenses"
              />
            </div>
          </div>
        </div>

        {/* Section 2: Leadership Risks */}
        <div className="mb-8 p-6 bg-gray-50 rounded-xl border-l-4 border-blue-700">
          <h3 className="text-xl font-semibold text-blue-800 mb-6">Step 2: Leadership & Succession Risks</h3>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block mb-2 text-gray-700 font-semibold text-sm">Key Leadership Positions</label>
              <NumberInput
                value={inputs.keyPositions}
                onChange={(v) => handleInputChange('keyPositions', v)}
                placeholder="8"
                helpText="CEO, CFO, COO, VP-level positions critical to operations"
              />
            </div>

            <div>
              <label className="block mb-2 text-gray-700 font-semibold text-sm">Leaders Eligible to Retire (Next 5 Years)</label>
              <NumberInput
                value={inputs.retirementEligible}
                onChange={(v) => handleInputChange('retirementEligible', v)}
                placeholder="3"
                helpText="Number of key leaders who could retire soon"
              />
            </div>

            <div>
              <label className="block mb-2 text-gray-700 font-semibold text-sm">Average Time to Fill Leadership Role (Days)</label>
              <NumberInput
                value={inputs.timeToFill}
                onChange={(v) => handleInputChange('timeToFill', v)}
                placeholder="120"
                helpText="Historical average or industry standard (typically 90-180 days)"
              />
            </div>

            <div>
              <label className="block mb-2 text-gray-700 font-semibold text-sm">Annual Leadership Turnover Rate</label>
              <PercentInput
                value={inputs.turnoverRate}
                onChange={(v) => handleInputChange('turnoverRate', v)}
                placeholder="12"
                helpText="Percentage of leadership positions that turn over annually"
              />
            </div>
          </div>

          <div className="mt-6">
            <label className="block mb-2 text-gray-700 font-semibold text-sm">Current Succession Planning Approach</label>
            <select
              value={inputs.currentApproach}
              onChange={(e) => handleInputChange('currentApproach', e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-base focus:outline-none focus:border-blue-700 focus:ring-2 focus:ring-blue-100 transition-all bg-white"
            >
              <option value="none">No formal succession planning</option>
              <option value="basic">Basic spreadsheet tracking</option>
              <option value="moderate">Some documented processes</option>
              <option value="advanced">Systematic but manual processes</option>
            </select>
          </div>
        </div>

        {/* Section 3: Cost Factors */}
        <div className="mb-8 p-6 bg-gray-50 rounded-xl border-l-4 border-blue-700">
          <h3 className="text-xl font-semibold text-blue-800 mb-6">Step 3: Current Costs & Impacts</h3>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block mb-2 text-gray-700 font-semibold text-sm">Average Executive Salary</label>
              <CurrencyInput
                value={inputs.avgExecSalary}
                onChange={(v) => handleInputChange('avgExecSalary', v)}
                placeholder="175,000"
                helpText="Average salary for key leadership positions"
              />
            </div>

            <div>
              <label className="block mb-2 text-gray-700 font-semibold text-sm">External Search Cost (as % of Salary)</label>
              <PercentInput
                value={inputs.searchCostPercent}
                onChange={(v) => handleInputChange('searchCostPercent', v)}
                placeholder="25"
                helpText="Typical range: 20-35% of first-year salary"
              />
            </div>

            <div>
              <label className="block mb-2 text-gray-700 font-semibold text-sm">Member Satisfaction Score (1-5)</label>
              <NumberInput
                value={inputs.memberSatisfaction}
                onChange={(v) => handleInputChange('memberSatisfaction', v)}
                placeholder="4.2"
                helpText="Current member satisfaction rating"
              />
            </div>

            <div>
              <label className="block mb-2 text-gray-700 font-semibold text-sm">Market Share in Primary Service Area</label>
              <PercentInput
                value={inputs.marketShare}
                onChange={(v) => handleInputChange('marketShare', v)}
                placeholder="20"
                helpText="Your estimated market share percentage"
              />
            </div>
          </div>

          <div className="mt-6">
            <label className="block mb-2 text-gray-700 font-semibold text-sm">Hours Spent Annually on Manual Succession Planning</label>
            <NumberInput
              value={inputs.manualHours}
              onChange={(v) => handleInputChange('manualHours', v)}
              placeholder="200"
              helpText="Board, HR, and executive time combined"
            />
          </div>
        </div>

        {/* Calculate Button */}
        <button
          onClick={calculateROI}
          className="w-full md:w-auto mx-auto block bg-gradient-to-r from-blue-800 to-blue-900 text-white px-10 py-4 rounded-full text-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all"
        >
          Calculate My Succession Planning Costs
        </button>

        {/* Warning Box */}
        <div className="mt-8">
          <WarningBox warnings={warnings} />
        </div>

        {/* Results Section */}
        {results && (
          <div className="mt-8 animate-fade-in">
            <h3 className="text-xl font-semibold text-blue-800 mb-6">Your Succession Planning Cost Analysis</h3>

            {/* Cost Breakdown */}
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm mb-6">
              <h4 className="text-lg font-semibold text-gray-800 mb-4">Annual Costs WITHOUT Succession Planning Software</h4>
              <CostItem label="Leadership Vacancy Costs" value={results.vacancyCost} />
              <CostItem label="External Search & Recruitment" value={results.recruitmentCost} />
              <CostItem label="Productivity Loss During Transitions" value={results.productivityCost} />
              <CostItem label="Member Impact & Market Share Loss" value={results.memberImpactCost} />
              <CostItem label="Manual Process Time Cost" value={results.manualProcessCost} />
              <CostItem label="Regulatory & Compliance Risk" value={results.complianceRisk} />
            </div>

            {/* Total Cost */}
            <div className="bg-gradient-to-r from-yellow-100 to-yellow-200 rounded-xl p-6 text-center mb-6">
              <h4 className="text-lg font-semibold text-gray-800 mb-2">Total Annual Cost of Poor Succession Planning</h4>
              <div className="text-4xl font-bold text-red-600">{formatCurrency(results.totalAnnualCost)}</div>
              <div className="text-gray-600">Per Year</div>
            </div>

            {/* Key Insights */}
            <div className="bg-green-50 rounded-xl p-6 border-2 border-green-500 mb-6">
              <h4 className="text-lg font-semibold text-gray-800 mb-4">What This Means for Your Credit Union</h4>
              <CostItem label="Cost per Member" value={results.costPerMember} />
              <div className="flex justify-between py-3 border-b border-gray-100">
                <span className="text-gray-600">Percentage of Operating Budget</span>
                <span className="font-semibold text-gray-800">{results.percentOfBudget.toFixed(1)}%</span>
              </div>
              <CostItem label="5-Year Cumulative Impact" value={results.fiveYearImpact} />
            </div>

            {/* Reduce These Costs */}
            <div className="bg-gradient-to-r from-dark-teal-400 to-dark-teal-600 rounded-xl p-6 text-white mb-6">
              <h4 className="text-xl font-bold mb-3">Reduce These Costs</h4>
              <p className="text-dark-teal-50 mb-6">
                SuccessionNow automates the succession planning process, helping to reduce these costs and save time.
              </p>

              <div className="bg-white rounded-lg p-6 text-center">
                <h5 className="text-lg font-semibold text-gray-800 mb-4">Your Quote</h5>
                <div className="text-5xl md:text-6xl font-bold text-dark-teal-600 mb-2">
                  {formatCurrency(getPriceByEmployees(results.inputs.numberOfEmployees))}
                </div>
                <p className="text-lg md:text-xl text-gray-600 font-medium">per year</p>
              </div>
            </div>

            {/* Strategic Opportunities */}
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm mb-6">
              <h4 className="text-lg font-semibold text-gray-800 mb-4">Strategic Opportunities with Better Succession Planning</h4>
              <ul className="list-disc ml-6 text-gray-600 space-y-2">
                <li><strong>Reduced Vacancy Time:</strong> Fill critical roles faster with ready successors</li>
                <li><strong>Less External Recruitment:</strong> Develop and promote internal talent</li>
                <li><strong>Improved NCUA Examinations:</strong> Demonstrate systematic succession planning</li>
                <li><strong>Enhanced Board Governance:</strong> Provide clear oversight and risk management</li>
                <li><strong>Increased Employee Retention:</strong> Show clear career advancement paths</li>
                <li><strong>Better Member Service:</strong> Maintain consistency during leadership transitions</li>
                <li><strong>Preserved Market Share:</strong> Avoid disruption that competitors can exploit</li>
                <li><strong>Reduced Key Person Risk:</strong> Build depth in critical positions</li>
              </ul>
            </div>

            {/* Assumptions */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <h4 className="text-yellow-700 font-semibold mb-2">Key Assumptions & Methodology</h4>
              <ul className="list-disc ml-5 text-gray-600 text-sm space-y-1">
                <li>Vacancy costs calculated at 2x salary for duration of vacancy (includes interim coverage & overtime)</li>
                <li>Productivity loss estimated at 25% for 6 months during leadership transitions</li>
                <li>Member impact based on 0.5-point satisfaction drop affecting 10% of member base</li>
                <li>External recruitment costs typically 20-35% of first-year salary</li>
                <li>Compliance risk estimated at 0.1% of assets for inadequate succession planning</li>
                <li>Manual process time valued at $75 per hour for board and executive time</li>
                <li>All calculations based on credit union industry benchmarks</li>
              </ul>
            </div>

            {/* Export Options */}
            <div className="text-center pt-6 border-t-2 border-gray-200">
              <div className="flex flex-wrap justify-center gap-4">
                <button
                  onClick={generatePDF}
                  className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-3 rounded-full font-semibold shadow hover:shadow-lg transform hover:-translate-y-0.5 transition-all"
                >
                  Download PDF Report
                </button>
                <button
                  onClick={exportJSON}
                  className="bg-gradient-to-r from-gray-500 to-gray-600 text-white px-6 py-3 rounded-full font-semibold shadow hover:shadow-lg transform hover:-translate-y-0.5 transition-all"
                >
                  Export Data (JSON)
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ROICalculator;
